// Runtime config for the dashboard. Reads from URL query params + localStorage.
//
// Auth model:
//   The dashboard SPA itself is gated by Cloudflare Access (only authed users
//   can load app.fan-management.com). Once loaded, the SPA needs a bearer
//   token to talk to the API worker on api.fan-management.com — the api
//   subdomain is intentionally NOT behind CF Access (so Electron + the
//   remote-agent can also reach it via WORKER_TOKEN bearer auth).
//
//   First visit: open https://app.fan-management.com/?token=<ADMIN_TOKEN>
//   The dashboard saves the token to localStorage, removes it from the URL,
//   and uses it for all subsequent fetches/WS connections. Future visits
//   reuse the localStorage value — no need to repeat the ?token= step.
//
// In dev: http://localhost:5173/?worker=main-pc&api=http://127.0.0.1:8787&token=dev-admin-token

const TOKEN_KEY = "fm-dashboard-auth-token";

const params = new URLSearchParams(window.location.search);

function readQueryToken(): string | null {
  // Parse the token directly from the raw query string. Avoid URLSearchParams
  // because it (per HTML form-encoding rules) decodes literal '+' as a space,
  // which corrupts base64 tokens that contain '+'.
  const search = window.location.search;
  const match = search.match(/[?&]token=([^&]*)/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function bootstrapToken(): string {
  // Priority: URL query param > localStorage > VITE_ADMIN_TOKEN baked into bundle.
  // The build-time env var means a fresh device with no localStorage just
  // works — no manual paste step. URL/localStorage still win so you can
  // override per-tab or per-device for testing/rotation.
  const fromUrl = readQueryToken();
  if (fromUrl) {
    try { localStorage.setItem(TOKEN_KEY, fromUrl); } catch { /* private mode */ }
    params.delete("token");
    const search = params.toString();
    const cleaned = location.pathname + (search ? "?" + search : "") + location.hash;
    window.history.replaceState(null, "", cleaned);
    return fromUrl;
  }
  try {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored) return stored;
  } catch { /* fall through to env default */ }
  return import.meta.env.VITE_ADMIN_TOKEN ?? "";
}

export const WORKER_ID = params.get("worker") ?? "main-pc";

export const API_BASE =
  params.get("api") ??
  (location.hostname === "localhost" || location.hostname === "127.0.0.1"
    ? "http://127.0.0.1:8787"
    : "https://api.fan-management.com");

export const WS_BASE = API_BASE.replace(/^http/, "ws");

export const AUTH_TOKEN = bootstrapToken();

export function hasAuthToken(): boolean {
  return AUTH_TOKEN.length > 0;
}

export function authHeaders(): HeadersInit {
  return AUTH_TOKEN ? { Authorization: `Bearer ${AUTH_TOKEN}` } : {};
}

export function withAuthToken(url: string): string {
  if (!AUTH_TOKEN) return url;
  const u = new URL(url, location.origin);
  if (!u.searchParams.has("token")) u.searchParams.set("token", AUTH_TOKEN);
  return u.toString();
}

export function clearAuthToken(): void {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* */ }
}
